/**
 * Frontend mirror of /app/django_backend/camps/pricing_resolver.py.
 *
 * One function: computePricing(selectedSessions, section, extendedCare, coupon).
 *
 * Business rules (must match backend exactly):
 *   • 1 session = 1 half-day slot (AM or PM, 3 hours)
 *   • Full day counts as 2 sessions
 *   • Price = tier.price × session_count, tier chosen by session_count range
 *   • Default tiers if section.pricing_tiers is empty:
 *       1 session → $69, 2-4 → $59, 5-9 → $49, 10+ → $45
 */

export const DEFAULT_TIERS = [
  { min_sessions: 1, max_sessions: 1, price: 69 },
  { min_sessions: 2, max_sessions: 4, price: 59 },
  { min_sessions: 5, max_sessions: 9, price: 49 },
  { min_sessions: 10, max_sessions: null, price: 45 },
];

export function normaliseTiers(raw) {
  if (!raw || !Array.isArray(raw) || raw.length === 0) return DEFAULT_TIERS;
  try {
    const out = raw.map(t => ({
      min_sessions: Number(t.min_sessions),
      max_sessions: t.max_sessions === null || t.max_sessions === '' ? null : Number(t.max_sessions),
      price: Number(t.price),
    }));
    out.sort((a, b) => a.min_sessions - b.min_sessions);
    return out;
  } catch {
    return DEFAULT_TIERS;
  }
}

export function tierFor(sessionCount, tiers) {
  if (!sessionCount || sessionCount <= 0) return tiers[0];
  for (const t of tiers) {
    if (sessionCount >= t.min_sessions && (t.max_sessions == null || sessionCount <= t.max_sessions)) {
      return t;
    }
  }
  return tiers[tiers.length - 1];
}

/** Count half-day-equivalent sessions from a selectedSessions map {dayNum: 'am'|'pm'|'full_day'}. */
export function countSessions(selectedSessions) {
  return Object.values(selectedSessions || {}).reduce((sum, s) => {
    if (s === 'full_day') return sum + 2;
    if (s === 'am' || s === 'pm') return sum + 1;
    return sum;
  }, 0);
}

/**
 * Compute cart pricing for a camp registration.
 *
 * @returns {{
 *   session_count: number,
 *   active_tier: object,
 *   tiers: object[],
 *   top_rate: number,
 *   active_rate: number,
 *   regularSubtotal: number,   // at the top (single-session) rate
 *   base: number,              // at the active tier rate (post-tier)
 *   bundleSavings: number,     // regularSubtotal - base
 *   extendedCare: number,
 *   discount: number,          // coupon discount
 *   final: number,             // base + extendedCare - discount
 * }}
 */
export function computePricing({
  selectedSessions = {},
  section = {},
  gradeBands = [],
  extendedCare = 'none',
  coupon = '',
}) {
  const tiers = normaliseTiers(section.effective_pricing_tiers || section.pricing_tiers);
  const sessionCount = countSessions(selectedSessions);
  const activeTier = tierFor(sessionCount, tiers);
  const topRate = Number(tiers[0].price);
  const activeRate = Number(activeTier.price);

  const regularSubtotal = sessionCount * topRate;
  const base = sessionCount * activeRate;
  const bundleSavings = Math.max(0, regularSubtotal - base);

  // Extended care — prorated at a per-session rate (half-day equivalents).
  let extendedCareAmt = 0;
  if (extendedCare !== 'none' && sessionCount > 0) {
    const isK2 = (gradeBands || []).includes('K-2');
    const weeklyRate = Number(section.extended_care_weekly ?? (isK2 ? 50 : 100));
    const is4Day = (section.location_name || '').match(/Bednarcik|Plank/i);
    const fullWeekSessions = is4Day ? 8 : 10; // 4 days × 2 or 5 days × 2
    const perSessionRate = weeklyRate / fullWeekSessions;
    const raw = perSessionRate * sessionCount;
    extendedCareAmt = extendedCare === 'both' ? raw : raw / 2;
  }

  const preDiscount = base + extendedCareAmt;
  // Coupon may be a string (legacy) or an object {discount_type, discount_value, code}
  // from the backend /api/coupons/validate/ endpoint. Both shapes supported.
  let couponPct = 0;
  let flatDiscount = 0;
  if (coupon && typeof coupon === 'object') {
    if (coupon.discount_type === 'percent') {
      couponPct = Number(coupon.discount_value || 0) / 100;
    } else if (coupon.discount_type === 'flat') {
      flatDiscount = Number(coupon.discount_value || 0);
    }
  } else if (typeof coupon === 'string') {
    // Legacy hardcoded codes (kept for back-compat — new coupons should go
    // through the object shape returned by /api/coupons/validate/).
    if (coupon === 'RETURNING20') couponPct = 0.20;
    else if (coupon === 'LOYALTY10') couponPct = 0.10;
  }
  let discount = couponPct ? Math.round(preDiscount * couponPct * 100) / 100 : 0;
  if (flatDiscount > 0) discount = Math.min(flatDiscount, preDiscount);

  return {
    session_count: sessionCount,
    active_tier: activeTier,
    tiers,
    top_rate: topRate,
    active_rate: activeRate,
    regularSubtotal: Math.round(regularSubtotal * 100) / 100,
    base: Math.round(base * 100) / 100,
    bundleSavings: Math.round(bundleSavings * 100) / 100,
    extendedCare: Math.round(extendedCareAmt * 100) / 100,
    discount,
    final: Math.round((preDiscount - discount) * 100) / 100,
  };
}
